import { Descriptions, Select, Table, TableColumnsType } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import useNotification from "../../../hooks/useNotification";
import useSeqItem from "../../../hooks/useSeqItem";
import { ISurveyQuestionDetail, ISurveyQuestionReq } from "../../../models/surveyQuestion.model";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import ConfirmModal from "../../modals/ConfirmModal";
import Radio from "../../Radio";
import SelectCampus from "../../SelectCampus";

interface IProps {
  submit: (data: ISurveyQuestionReq) => void;
}

const SurveyQuestionEditor: FC<IProps> = ({
  submit,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { currentCampus, isDarkMode } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // HP0006 = 설문문항 유형
  const HP0006 = commonCodes.find(item => item.commonCode === 'HP0006')?.detailCode;  
  // HP0007 = 설문문항 카테고리
  const HP0007 = commonCodes.find(item => item.commonCode === 'HP0007')?.detailCode;
  const { raiseSeqItem, lowerSeqItem } = useSeqItem();
  const { infoNotification } = useNotification();

  const [title, setTitle] = useState('');
  const [surveyCategory, setSurveyCategory] = useState<string>();
  const [questionType, setQuestionType] = useState<string>('01');
  const [useYn, setUseYn] = useState('Y');
  const [questionDetail, setQuestionDetail] = useState<ISurveyQuestionDetail[]>([]);
  const [confirmSubmitVisible, setConfirmSubmitVisible] = useState(false);

  const defaultQuestionDetail = [
    {
      questionContent: t('homepage.surveyQuestion.text1'),
      score: 10,
      seq: 1,
    },
    {
      questionContent: t('homepage.surveyQuestion.text2'),
      score: 8,
      seq: 2,
    },
    {
      questionContent: t('homepage.surveyQuestion.text3'),
      score: 6,
      seq: 3,
    },
    {
      questionContent: t('homepage.surveyQuestion.text4'),
      score: 4,
      seq: 4,
    },
    {
      questionContent: t('homepage.surveyQuestion.text5'),
      score: 2,
      seq: 5,
    },
  ];

  useEffect(() => {
    setQuestionDetail(defaultQuestionDetail);
  }, []);

  const onSetQuestionDetail = (index: number, key: string, value: any) => {
    const newQuestionDetail: any = [...questionDetail];
    newQuestionDetail[index][key] = value;
    setQuestionDetail(newQuestionDetail);
  }

  const onAddAnswer = () => {
    const newQuestionDetail: any = [...questionDetail];
    newQuestionDetail.push({
      questionContent: '',
      score: 0,
      seq: newQuestionDetail.length + 1,
    });
    setQuestionDetail(newQuestionDetail);
  }

  const onDeleteAnswer = (index: number) => {
    const newQuestionDetail: any = [...questionDetail];
    newQuestionDetail.splice(index, 1);
    setQuestionDetail(newQuestionDetail);
  }

  const onCheckReq = () => {
    if (!title) {
      return infoNotification(t('homepage.surveyQuestion.message1'));
    }
    if (!surveyCategory) {
      return infoNotification(t('homepage.surveyQuestion.message2'));
    }
    if (questionType === '01') {
      const isQuestionContentEmpty = questionDetail.some(item => !item.questionContent);
      if (isQuestionContentEmpty) {
        return infoNotification(t('homepage.surveyQuestion.message3'));
      }
    }

    setConfirmSubmitVisible(true);
  }

  const onSubmit = () => {
    const req: any = {
      title,
      campusCode: currentCampus,
      surveyCategory,
      questionType,
      useYn,      
    };

    if (questionType === '01') {
      req.questionDetail = questionDetail;
    }

    submit(req);
  }

  const columns: TableColumnsType<ISurveyQuestionDetail> = [
    {
      title: t('homepage.survey.column11'),
      dataIndex: 'questionContent',
      key: 'questionContent',
      render: (value, record, index) => (
        <input
          type="text"
          className="form-control"
          value={value}
          onChange={e => onSetQuestionDetail(index, 'questionContent', e.target.value)}
        />
      ),
    },
    {
      title: t('common.column.score'),
      dataIndex: 'score',
      key: 'score',
      align: 'center',
      render: (value, record, index) => (
        <Select
          className="w-100p"
          placeholder={t('common.column.category')}
          value={value}
          onChange={value => onSetQuestionDetail(index, 'score', value)}
        >
          {[...Array(11)].map((item: any, index: number) => {
            return (
              <Select.Option key={index} value={index}>
                {index}
              </Select.Option>
            )
          })}
        </Select>
      ),
    },
    {
      title: t('homepage.survey.column4'),
      dataIndex: 'seq',
      key: 'seq',
      align: 'center',
      render: value => (
        <div className="order-sett__area">
          <a
            className={value === 1 ? 'btn-order up disabled' : 'btn-order up'}
            onClick={() => {
              if (value === 1) return;
              setQuestionDetail(raiseSeqItem(questionDetail, 'seq', value));
            }}
          />
          <a
            className={value === questionDetail.length ? 'btn-order down disabled' : 'btn-order down'}
            onClick={() => {
              if (value === questionDetail.length) return;
              setQuestionDetail(lowerSeqItem(questionDetail, 'seq', value));
            }}
          />
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'seq',
      key: 'seq',
      align: 'center',
      render: (value, record, index) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className='btn-basic small gray'
            onClick={() => onDeleteAnswer(index)}
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      ),
    },
  ];

  return (
    <>
    <div className='comm-table__area rowType'>
      <Descriptions
        bordered
        column={4}
        size="small"
        labelStyle={{ width: '12.5%' }}
        contentStyle={{ width: '12.5%' }}
      >
        <Descriptions.Item label={t('homepage.survey.column12')} span={3}>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.surveyQuestion.column1')}>
          {moment(serverDate).format('YYYY.MM.DD')}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.menu.campus')}>
          <SelectCampus />
        </Descriptions.Item>
        <Descriptions.Item label={t('common.column.category')}>
          <Select
            className="w-100p"
            placeholder={t('common.button.select')}
            value={surveyCategory}
            onChange={value => setSurveyCategory(value)}
          >
            {HP0007?.map((item, index) => (
              <Select.Option
                key={index}
                value={item.detailCode}
              >
                {item.korName}
              </Select.Option>
            ))}
          </Select>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.survey.column1')}>
          <Select
            className="w-100p"
            placeholder={t('common.button.select')}
            value={questionType}
            onChange={value => setQuestionType(value)}
          >
            {HP0006?.map((item, index) => (
              <Select.Option
                key={index}
                value={item.detailCode}
              >
                {item.korName}
              </Select.Option>
            ))}
          </Select>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.surveyQuestion.column2')}>
          <Radio
            checked={useYn === 'Y'}
            onChange={() => setUseYn('Y')}
            text={t('common.option.yes')}
          />
          <Radio
            className="ml-4"
            checked={useYn === 'N'}
            onChange={() => setUseYn('N')}
            text={t('common.option.no')}
          />
        </Descriptions.Item>
      </Descriptions>
    </div>
      {
        questionType === '01'
          ? (
            <div className='comm-table__area mt-3'>
              <Table
                columns={columns}
                dataSource={questionDetail}
                pagination={{ pageSize: 99999 }}
              />
            </div>
            )
          : null
      }

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            onClick={() => history.push(`/homepage/survey-question${location.search}`)}
            className="btn-basic gray-line"
            text={t('common.button.back')}
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            onClick={onCheckReq}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          {
            questionType === '01'
              ? (
                <Button
                  onClick={onAddAnswer}
                  className="btn-basic pink-line"
                  text={t('homepage.survey.button2')}
                  writeAuthYn={writeAuthYn}
                />
                )
              : null
          }
        </div>
      </div>

      {confirmSubmitVisible &&
        <ConfirmModal
          visible={confirmSubmitVisible}
          setVisible={setConfirmSubmitVisible}
          onConfirm={onSubmit}
          titleText={t('homepage.surveyQuestion.title3')}
          messageText={t('homepage.surveyQuestion.text6')}
          confirmText={t('common.button.save')}
          cancelText={t('common.button.cancel')}
        />
      }
    
    </>
  );
}

export default SurveyQuestionEditor;
